import React, { useEffect, useState } from 'react';
import { useUserInfo } from '@repository/auth/useUserInfo';
import KeywordModal from '@components/phs/test/keywordModal';
import EmptySkills from '@components/phs/test/skillPositionList/EmptySkills';
import LoginModal from '@components/loginModal';
import Position from '@components/phs/test/skillPositionList/Position';
import classnames from 'classnames/bind';
import styles from './skillPositionList.module.scss';
import { usePhsCenterSkill, usePhsCenterSkillPosition } from '@repository/phs/center/usePhsCenter';

const cx = classnames.bind(styles);

export type ModalType = 'Login' | 'SkillKeyword' | 'MatchingTerms' | null;

const EmptyArea = () => {
  return (
    <div className={cx('emptyInner')}>
      <em className={cx('emptyTitle')}>포지션을 기다리고 있어요.</em>
      <p className={cx('emptyDesc')}>입력한 기술 키워드를 활용할 수 있는 채용이 시작하면 포지션을 확인할 수 있어요.</p>
    </div>
  );
};

const SkillPositionList = () => {
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;
  const [selectedSkillCode, setSelectedSkillCode] = useState<number | undefined>(undefined);
  const [modalType, setModalType] = useState<ModalType>(null);

  const { data: skillData } = usePhsCenterSkill(isLogin);
  const { data: skillPositionData } = usePhsCenterSkillPosition(
    { page: 0, size: 12, skillCode: selectedSkillCode },
    selectedSkillCode !== null && isLogin,
  );

  const isVisible = (type: ModalType) => modalType === type;
  const skills = skillData?.skills || [];
  const isSkills = skills.length > 0;
  const isPosition = skills.filter((skill) => skill.positionCount !== 0).length > 0;
  const getSkillName = () => skillData?.skills.find((skill) => skill.code === selectedSkillCode)?.keyword || '';
  const titleText = isLogin ? (
    <>{userData?.name}님이 가진 기술을 활용할 수 있는 포지션이에요.</>
  ) : (
    <>나의 기술 키워드를 활용할 수 있는 포지션이에요.</>
  );

  useEffect(() => {
    const filteredSkills = skills.filter((skill) => skill.positionCount !== 0);
    if (filteredSkills.length > 0) setSelectedSkillCode(filteredSkills[0].code);
  }, [skillData, isSkills, skills]);

  const handleSkillKeyword = () => {
    if (!isLogin) {
      setModalType('Login');
      return;
    }

    setModalType('SkillKeyword');
  };

  const handleClose = () => {
    setModalType(null);
  };

  const getSkillEls = () => {
    return skillData?.skills
      .filter((skill) => skill.positionCount !== 0)
      .map((skill) => {
        return (
          <a
            key={skill.code}
            className={cx('btnSkill', { on: selectedSkillCode === skill.code })}
            role="button"
            onClick={() => setSelectedSkillCode(skill.code)}
          >
            {skill.keyword}
          </a>
        );
      });
  };

  const getContentsEl = () => {
    // [D] 미로그인 또는 입력한 기술키워드 없는 경우
    if (!isLogin || !isSkills) return <EmptySkills titleText={titleText} handleSkillKeyword={handleSkillKeyword} />;

    return (
      <>
        {/* 타이틀 영역 */}
        <div className={cx('titleArea')}>
          <strong className={cx('title')}>{titleText}</strong>
          {isPosition && <p className={cx('desc')}>{getSkillName()} 기술을 요구하는 포지션이에요.</p>}
        </div>

        {/* 스킬 리스트 영역 */}
        <div className={cx('skillListArea')}>
          <div className={cx('skillList')}>{getSkillEls()}</div>

          <a className={cx('btnKeyword')} role="button" onClick={handleSkillKeyword}>
            보유한 기술 키워드 입력하기
          </a>
        </div>

        {/* 포지션 리스트 영역 */}
        {isPosition ? <Position positions={skillPositionData?.positions} /> : <EmptyArea />}
      </>
    );
  };

  return (
    <div className={cx('skillPositionListArea')}>
      {getContentsEl()}

      <LoginModal
        subTitle="채용정보 · 역량검사 · 매칭까지"
        title="취업기회 지금 바로 잡다!"
        visible={isVisible('Login')}
        onClose={handleClose}
      />

      {/* 기술 키워드 입력 모달 */}
      <KeywordModal visible={isVisible('SkillKeyword')} onClose={handleClose} />
    </div>
  );
};

export default SkillPositionList;
